import type { TenantRead } from "@harmonyso/tenants-sdk";
import {
  CELLS,
  PRIMARY_CELL,
  isRemoteCell,
  type Cell,
} from "@/lib/cells/cells";

export interface BroadcastTenantResult {
  tenant_id: string;
  company: string | null;
  success: boolean;
  announcement_id: string | null;
  error: string | null;
}

/** A cell whose whole request failed, so its tenants aren't in `results`. */
export interface BroadcastCellError {
  cellId: string;
  error: string;
}

export interface BroadcastResponse {
  idempotent_id: string;
  total: number;
  succeeded: number;
  failed: number;
  results: BroadcastTenantResult[];
  cellErrors?: BroadcastCellError[];
}

export interface BroadcastHistoryItem {
  idempotent_id: string;
  title: string;
  body: string;
  announcement_type: string;
  cta_link: string | null;
  publish_at: string | null;
  expire_at: string | null;
  created_at: string;
  tenant_count: number;
  published_count: number;
}

export interface CellTenantGroup {
  cell: Cell;
  tenantIds: string[];
}

/** Starts the error of a tenant that was never sent, which a retry can't fix. */
export const NOT_SENT_PREFIX = "Not sent:";

/**
 * Splits a broadcast's tenants by the cell each lives in, in `cells` order.
 * Each cell's backoffice can deliver only to its own tenants. A tenant in no
 * known cell goes to `unplaced` and must not be sent anywhere. With a single
 * cell every tenant goes to it, as before cells existed.
 */
export function groupTenantIdsByCell(
  tenantIds: string[],
  tenants: Pick<TenantRead, "id" | "deployment_cell">[],
  cells: Cell[] = CELLS,
): { groups: CellTenantGroup[]; unplaced: string[] } {
  if (cells.length === 1) {
    return {
      groups:
        tenantIds.length > 0
          ? [{ cell: cells[0] ?? PRIMARY_CELL, tenantIds }]
          : [],
      unplaced: [],
    };
  }

  const byCell = new Map(cells.map((cell) => [cell.id, [] as string[]]));
  const cellOf = new Map(
    tenants.map((tenant) => [tenant.id, tenant.deployment_cell]),
  );
  const unplaced: string[] = [];
  for (const tenantId of tenantIds) {
    const cellId = cellOf.get(tenantId);
    const bucket = cellId ? byCell.get(cellId) : undefined;
    if (bucket) {
      bucket.push(tenantId);
    } else {
      unplaced.push(tenantId);
    }
  }
  return {
    groups: cells.flatMap((cell) => {
      const ids = byCell.get(cell.id) ?? [];
      return ids.length > 0 ? [{ cell, tenantIds: ids }] : [];
    }),
    unplaced,
  };
}

export type CellSendOutcome =
  | { group: CellTenantGroup; response: BroadcastResponse }
  | { group: CellTenantGroup; error: string };

const failedResults = (
  tenantIds: string[],
  error: string,
): BroadcastTenantResult[] =>
  tenantIds.map((tenant_id) => ({
    tenant_id,
    company: null,
    success: false,
    announcement_id: null,
    error,
  }));

/**
 * One result for a broadcast sent to several cells. A failed request for
 * named tenants counts each of them as failed, so they can be retried. A
 * failed stage request, whose tenants nobody knows, becomes a cell error. A
 * tenant no cell has fails with NOT_SENT_PREFIX.
 */
export function mergeCellSends(
  idempotentId: string,
  outcomes: CellSendOutcome[],
  unplaced: string[],
  unavailableCellIds: string[] = [],
): BroadcastResponse {
  const results: BroadcastTenantResult[] = [];
  const cellErrors: BroadcastCellError[] = [];
  for (const outcome of outcomes) {
    if ("response" in outcome) {
      results.push(...outcome.response.results);
    } else if (outcome.group.tenantIds.length > 0) {
      results.push(
        ...failedResults(
          outcome.group.tenantIds,
          `${outcome.group.cell.id} request failed: ${outcome.error}`,
        ),
      );
    } else {
      cellErrors.push({ cellId: outcome.group.cell.id, error: outcome.error });
    }
  }
  const unavailable =
    unavailableCellIds.length > 0
      ? ` (${unavailableCellIds.join(", ")} couldn't be listed)`
      : "";
  results.push(
    ...failedResults(
      unplaced,
      `${NOT_SENT_PREFIX} couldn't tell which cell this tenant is in${unavailable}`,
    ),
  );
  const succeeded = results.filter((result) => result.success).length;
  return {
    idempotent_id: idempotentId,
    total: results.length,
    succeeded,
    failed: results.length - succeeded,
    results,
    ...(cellErrors.length > 0 && { cellErrors }),
  };
}

/**
 * Sends a broadcast to every cell it reaches. `post` makes one request: the
 * named tenants (or a stage audience when undefined) to the given cell. A
 * stage audience goes to every cell, each resolving its own customers; named
 * tenants go to the cell each lives in. With a single cell, or named tenants
 * all in the primary cell, it is the one request it always was. When every
 * cell's request fails, the first error is thrown, as the single request's is.
 */
export async function sendToCells({
  idempotentId,
  tenantIds,
  tenants,
  post,
  errorDetail,
  unavailableCellIds = [],
  cells = CELLS,
}: {
  idempotentId: string;
  tenantIds: string[] | undefined;
  tenants: Pick<TenantRead, "id" | "deployment_cell">[];
  post: (
    tenantIds: string[] | undefined,
    routeCellId?: string,
  ) => Promise<BroadcastResponse>;
  errorDetail: (error: unknown) => string;
  unavailableCellIds?: string[];
  cells?: Cell[];
}): Promise<BroadcastResponse> {
  let groups: CellTenantGroup[];
  let unplaced: string[] = [];
  if (!tenantIds) {
    if (cells.length === 1) {
      // A retry may name just one other cell; that one still needs its route.
      const [only] = cells;
      return isRemoteCell(only) ? post(undefined, only.id) : post(undefined);
    }
    groups = cells.map((cell) => ({ cell, tenantIds: [] }));
  } else {
    ({ groups, unplaced } = groupTenantIdsByCell(tenantIds, tenants, cells));
    if (
      unplaced.length === 0 &&
      groups.every((group) => !isRemoteCell(group.cell))
    ) {
      return post(tenantIds);
    }
  }

  // The same idempotent id goes to every cell; each dedupes its own tenants.
  const errors: unknown[] = [];
  const outcomes = await Promise.all(
    groups.map(async (group): Promise<CellSendOutcome> => {
      try {
        return {
          group,
          response: await post(
            tenantIds ? group.tenantIds : undefined,
            group.cell.id,
          ),
        };
      } catch (error) {
        errors.push(error);
        return { group, error: errorDetail(error) };
      }
    }),
  );
  if (
    outcomes.length > 0 &&
    errors.length === outcomes.length &&
    unplaced.length === 0
  ) {
    throw errors[0];
  }
  return mergeCellSends(idempotentId, outcomes, unplaced, unavailableCellIds);
}

/** Broadcast history from every cell, one row per logical broadcast. */
export function mergeHistories<
  T extends {
    idempotent_id: string;
    created_at: string;
    tenant_count: number;
    published_count: number;
  },
>(histories: { items: T[] }[]): { total: number; items: T[] } {
  const byId = new Map<string, T>();
  for (const item of histories.flatMap((history) => history.items)) {
    const seen = byId.get(item.idempotent_id);
    byId.set(
      item.idempotent_id,
      seen
        ? {
            ...seen,
            tenant_count: seen.tenant_count + item.tenant_count,
            published_count: seen.published_count + item.published_count,
          }
        : item,
    );
  }
  const items = [...byId.values()].sort((a, b) =>
    b.created_at.localeCompare(a.created_at),
  );
  return { total: items.length, items };
}

/**
 * A retry of the cells a stage send failed in, folded into the first result:
 * the earlier cells' tenants stay, the retried cells' outcome replaces them.
 */
export function mergeRetry(
  previous: BroadcastResponse,
  retry: BroadcastResponse,
): BroadcastResponse {
  const results = [...previous.results, ...retry.results];
  const succeeded = results.filter((result) => result.success).length;
  return {
    idempotent_id: previous.idempotent_id,
    total: results.length,
    succeeded,
    failed: results.length - succeeded,
    results,
    ...(retry.cellErrors &&
      retry.cellErrors.length > 0 && { cellErrors: retry.cellErrors }),
  };
}
